import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Clock, ArrowRight } from "lucide-react";
import SEO, { breadcrumbSchema, localBusinessSchema } from "../components/SEO";
import { SectionLabel, Breadcrumbs, CTASection } from "../components/Shared";
import { IMAGES } from "../data/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const crumbs = [{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }];

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");

  useEffect(() => {
    axios.get(`${API}/posts`).then((r) => setPosts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const categories = [...new Set(posts.map((p) => p.category))];
  const filtered = category ? posts.filter((p) => p.category === category) : posts;

  return (
    <>
      <SEO
        title="Garage Floor Coating Blog | Phoenix Guides & Pricing | Vantyx"
        description="Expert guides on garage floor coatings for Phoenix homeowners: polyaspartic vs epoxy, 2025 pricing, UV stability, concrete prep, and Arizona color guides."
        path="/blog"
        ogImage={IMAGES.garage2}
        schemas={[breadcrumbSchema(crumbs), localBusinessSchema()]}
      />

      <section className="hero-gradient py-20 md:py-24 px-6 md:px-12 grain relative" data-testid="blog-hero">
        <div className="max-w-7xl mx-auto">
          <Breadcrumbs items={crumbs} />
          <SectionLabel>The Vantyx Blog</SectionLabel>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white">
            Garage Floor Intelligence, <span className="text-[#028090]">Arizona Edition</span>
          </h1>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-3 mb-12" data-testid="blog-category-filters">
            <button onClick={() => setCategory("")} data-testid="blog-category-all" className={`text-xs uppercase tracking-wider font-bold px-4 py-2 border transition-colors duration-200 ${!category ? "bg-[#028090] text-white border-[#028090]" : "border-gray-300 text-gray-600 hover:border-[#028090] hover:text-[#028090]"}`}>All</button>
            {categories.map((c) => (
              <button key={c} onClick={() => setCategory(c)} data-testid={`blog-category-${c.toLowerCase().replace(/\s/g, "-")}`} className={`text-xs uppercase tracking-wider font-bold px-4 py-2 border transition-colors duration-200 ${category === c ? "bg-[#028090] text-white border-[#028090]" : "border-gray-300 text-gray-600 hover:border-[#028090] hover:text-[#028090]"}`}>{c}</button>
            ))}
          </div>
          {loading ? (
            <p className="text-gray-500">Loading articles…</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((p) => (
                <article key={p.slug} className="border border-gray-200 hover:-translate-y-1 transition-transform duration-300 flex flex-col" data-testid={`blog-card-${p.slug}`}>
                  <Link to={`/blog/${p.slug}`}>
                    <img src={p.image} alt={p.image_alt} loading="lazy" className="w-full aspect-[16/9] object-cover" />
                  </Link>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 text-xs mb-3">
                      <span className="bg-[#028090]/10 text-[#028090] font-bold uppercase tracking-wider px-2.5 py-1">{p.category}</span>
                      <span className="flex items-center gap-1 text-gray-400"><Clock size={12} /> {p.read_time} min read</span>
                    </div>
                    <h2 className="font-display text-lg font-bold text-[#000000] leading-snug">
                      <Link to={`/blog/${p.slug}`} className="hover:text-[#028090] transition-colors duration-200">{p.title}</Link>
                    </h2>
                    <p className="text-gray-500 text-sm mt-3 leading-relaxed flex-1">{p.excerpt}</p>
                    <Link to={`/blog/${p.slug}`} data-testid={`blog-read-${p.slug}`} className="inline-flex items-center gap-2 text-[#028090] font-bold text-xs uppercase tracking-wider mt-5 hover:gap-3 transition-all">
                      Read Article <ArrowRight size={14} />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <CTASection title="Done Reading? Get a Real Price." subtitle="Free in-home estimate, fixed written pricing, one-day install." />
    </>
  );
}
